<section class="title-row">
  <h2 class="typography-sub-heading" [translate]="newPaymentCard ? 'AddPaymentCard' : 'EditPaymentCard:Title'"></h2>
  <img class="secure-badge" src="assets/images/my-profile/secure-badge2x.png" alt="secure badge" />
</section>

<form [formGroup]="paymentCardForm" [class.edit]="!newPaymentCard">
  <h4>{{ 'ProfileDetails:CardDetailsTitle' | translate }}</h4>

  <!-- Card type -->
  <mat-form-field class="input-container">
    <mat-select [placeholder]="'CreditCardFormComponent:CardType' | translate" formControlName="card_type">
      <mat-option
        *ngFor="let type of paymentCardTypes$ | async"
        [value]="type.name"
        (onSelectionChange)="updateCardType(type, $event)"
      >
        <span>{{ type.name }}</span>
      </mat-option>
    </mat-select>
    <mat-error
      role="alert"
      when="touched"
      ngxError="required"
      ngxErrors="card_type"
      [translate]="'UserDetailsComponent:CardTypeRequired'"
    >
      Please select card type
    </mat-error>
  </mat-form-field>

  <!-- Card number's last 4 digits (edit mode only) -->
  <mat-form-field class="input-container" *ngIf="!newPaymentCard">
    <input
      matInput
      disabled
      [value]="('ProfileCard:Last4Digits' | translate) + ' ' + (paymentCardToEdit?.card_number || '' | slice: -4)"
    />
  </mat-form-field>

  <!-- Tokeniser iFrame -->
  <iframe
    *ngIf="tokeniserURL?.stringValue$ | async as iframeUrl; else loading"
    [src]="iframeUrl | safeUrl"
    frameborder="0"
    referrerpolicy="origin"
    tenTokenizerErrorListener
    (tokenizerError)="onIframeError()"
    tenTokenizerMessageListener
    (tokenizerMessage)="onMessage($event)"
    title="payment card info"
  ></iframe>
</form>
<form [formGroup]="paymentCardForm" [class.edit]="!newPaymentCard" class="billing-address-form">
  <h4>{{ 'ProfileDetails:BillingAddressTitle' | translate }}</h4>

  <ng-container formGroupName="address">
    <!-- House number -->
    <mat-form-field class="input-container" *ngIf="newPaymentCard">
      <input
        [placeholder]="'AddressFormComponent:HouseNumber' | translate"
        autocomplete="off"
        matInput
        formControlName="house_number"
      />
      <mat-error
        role="alert"
        when="dirty"
        ngxError="maxlength"
        ngxErrors="address.house_number"
        [translate]="'AddressFormComponent:HouseNumberErrorMsg'"
      >
        Maximum of 10 digits
      </mat-error>
      <mat-error
        role="alert"
        when="touched"
        ngxError="pattern"
        ngxErrors="address.house_number"
        [translate]="'TravellerFormComponent:FormAZErrorMsg'"
      >
        Please only use a-z, hyphens, apostrophes and spaces.
      </mat-error>
    </mat-form-field>

    <!-- Address line 1 -->
    <mat-form-field class="input-container">
      <input
        matInput
        autocomplete="address-line1"
        formControlName="street1"
        [placeholder]="'AddressFormComponent:AddressFirstLinePlaceholder' | translate"
      />
      <mat-error
        role="alert"
        when="touched"
        ngxError="required"
        ngxErrors="address.street1"
        [translate]="'AddressFormComponent:AddressFirstLineErrorMsg'"
      >
        Please enter the first line of your address
      </mat-error>
      <mat-error
        role="alert"
        when="touched"
        ngxError="pattern"
        ngxErrors="address.street1"
        [translate]="'TravellerFormComponent:FormAZErrorMsg'"
      >
        Please only use a-z, hyphens, apostrophes and spaces.
      </mat-error>
    </mat-form-field>

    <!-- Address line 2 -->
    <mat-form-field class="input-container">
      <input
        autocomplete="address-line2"
        matInput
        formControlName="street2"
        [placeholder]="'AddressFormComponent:AddressSecondLinePlaceholder' | translate"
      />
      <mat-error
        role="alert"
        when="touched"
        ngxError="pattern"
        ngxErrors="address.street2"
        [translate]="'TravellerFormComponent:FormAZErrorMsg'"
      >
        Please only use a-z, hyphens, apostrophes and spaces.
      </mat-error>
    </mat-form-field>

    <!-- City -->
    <mat-form-field class="input-container half-width">
      <input
        [placeholder]="'AddressFormComponent:AddressCityLabel' | translate"
        autocomplete="address-level2"
        matInput
        formControlName="city"
      />
      <mat-error
        role="alert"
        when="touched"
        ngxError="required"
        ngxErrors="address.city"
        [translate]="'AddressFormComponent:AddressCityErrorMsg'"
      >
        Please enter your city
      </mat-error>
      <mat-error
        role="alert"
        when="touched"
        ngxError="pattern"
        ngxErrors="address.city"
        [translate]="'TravellerFormComponent:FormAZErrorMsg'"
      >
        Please only use a-z, hyphens, apostrophes and spaces.
      </mat-error>
    </mat-form-field>

    <!-- State -->
    <mat-form-field class="input-container half-width">
      <input
        matInput
        autocomplete="address-level1"
        formControlName="state"
        [required]="stateRequired"
        [placeholder]="'AddressFormComponent:AddressStateorProviceLabel' | translate"
      />
      <mat-error
        role="alert"
        when="touched"
        ngxError="required"
        ngxErrors="address.state"
        [translate]="'UserDetailsComponent:StateReq'"
      >
        Please enter your State/Province
      </mat-error>
      <mat-error
        role="alert"
        when="touched"
        ngxError="pattern"
        ngxErrors="address.state"
        [translate]="'TravellerFormComponent:FormAZErrorMsg'"
      >
        Please only use a-z, hyphens, apostrophes and spaces.
      </mat-error>
    </mat-form-field>

    <!-- Country -->
    <mat-form-field class="input-container half-width">
      <input
        required
        matInput
        type="text"
        autocomplete="country"
        formControlName="country"
        [matAutocomplete]="countryAutocomplete"
        [placeholder]="'UserDetailsComponent:Country' | translate"
        (change)="checkCountry(countryAutocomplete.isOpen)"
      />
      <mat-autocomplete
        hideSingleSelectionIndicator="true"
        #countryAutocomplete="matAutocomplete"
        (closed)="checkCountry(false)"
      >
        <mat-option *ngFor="let country of filteredCountries$ | async" [value]="country.name">
          {{ country.name }}
        </mat-option>
      </mat-autocomplete>
      <mat-error
        role="alert"
        when="touched"
        ngxError="required"
        ngxErrors="address.country"
        [translate]="'AddressFormComponent:AddressCountryErrorMsg'"
      >
        Please select a country
      </mat-error>
    </mat-form-field>

    <!-- Post code -->
    <mat-form-field class="input-container half-width">
      <input
        autocomplete="postal-code"
        matInput
        formControlName="post_code"
        [placeholder]="'AddressFormComponent:AddressPostcodeLabel' | translate"
      />
      <mat-error
        role="alert"
        when="touched"
        ngxError="required"
        ngxErrors="address.post_code"
        [translate]="'AddressFormComponent:AddressPostcodeErrorMsg'"
      >
        Please enter your Zip code/Postcode
      </mat-error>
      <mat-error
        role="alert"
        when="touched"
        ngxError="pattern"
        ngxErrors="address.post_code"
        [translate]="'TravellerFormComponent:FormAZErrorMsg'"
      >
        Please only use a-z, hyphens, apostrophes and spaces.
      </mat-error>
    </mat-form-field>
  </ng-container>

  <!-- Preferred -->
  <mat-checkbox formControlName="is_default_card">
    <ng-container>{{ 'ProfileDetails:PreferredCardCTA' | translate }}</ng-container>
  </mat-checkbox>

  <!-- Store card // Temporarily removing checkbox until we can save cards for offline use -->
  <!--
  <mat-checkbox formControlName="offline_use">
    <ng-container>{{ 'ProfileDetails:SaveCardForLMsMessage' | translate }}</ng-container>
  </mat-checkbox>
  -->

  <section class="submit-row top-separator">
    <button
      *ngIf="!newPaymentCard"
      mat-raised-button
      class="mr-2 remove-button"
      [attr.aria-label]="'ProfileDetails:Delete' | translate"
      (click)="removePaymentCard()"
    >
      {{ 'ProfileDetails:Delete' | translate }}
    </button>
    <div class="action-buttons" [ngClass]="{ 'action-buttons--has-seperator': !newPaymentCard }">
      <button
        mat-raised-button
        id="cancel-current-payment-card-edit"
        class="close-button"
        [attr.aria-label]="'PopupForm:Close' | translate"
        (click)="closeForm()"
      >
        {{ 'PopupForm:Close' | translate }}
      </button>
      <button
        mat-raised-button
        id="save-current-payment-card-edit"
        color="primary"
        [attr.aria-label]="'PopupForm:Submit' | translate"
        (click)="submitForm()"
        type="submit"
      >
        {{ 'PopupForm:Submit' | translate }}
      </button>
    </div>
  </section>
</form>

<ng-template #loading>
  <div class="iframe-loader">
    <mat-spinner [diameter]="60"></mat-spinner>
  </div>
</ng-template>
